<template>
  <div class="animated fadeIn">

    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="fa fa-users mr-2 text-info"></i>Tech Team Members</h4>
        <small class="text-muted">Manage support team for issue assignment</small>
      </b-col>
      <b-col class="text-right">
        <b-button variant="info" size="sm" @click="openCreate">
          <i class="icon-user-follow mr-1"></i>Add Member
        </b-button>
      </b-col>
    </b-row>

    <!-- Members Table -->
    <b-card no-body class="shadow-sm">
      <b-table
        :items="members"
        :fields="fields"
        :busy="loading"
        responsive striped hover show-empty
        empty-text="No team members found."
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center py-4"><b-spinner variant="info" /> Loading…</div>
        </template>
        <template #cell(full_name)="d">
          <div class="d-flex align-items-center">
            <span class="avatar-sm mr-3">{{ d.value ? d.value.charAt(0) : '?' }}</span>
            <div>
              <strong>{{ d.value }}</strong>
              <div class="text-muted" style="font-size:.75rem">{{ d.item.username }}</div>
            </div>
          </div>
        </template>
        <template #cell(email)="d">
          <span v-if="d.value" class="text-muted small">{{ d.value }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(phone)="d">
          <span v-if="d.value" class="small">{{ d.value }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(is_active)="d">
          <b-badge :variant="d.value ? 'success' : 'secondary'">
            {{ d.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
        <template #cell(actions)="d">
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(d.item)">
            <i class="icon-pencil"></i>
          </b-button>
          <b-button
            size="sm"
            :variant="d.item.is_active ? 'outline-danger' : 'outline-success'"
            @click="toggleActive(d.item)"
          >
            <i :class="d.item.is_active ? 'icon-close' : 'icon-check'"></i>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="editingId ? 'Edit Team Member' : 'Add New Member'"
      @ok="submitModal"
      @hidden="resetModal"
      :ok-title="editingId ? 'Save Changes' : 'Add Member'"
      ok-variant="info"
      cancel-variant="outline-secondary"
      :ok-disabled="modalSaving"
    >
      <b-form @submit.stop.prevent>

        <b-form-group label="Username *" description="Used for login and issue assignment">
          <b-form-input
            v-model.trim="modalForm.username"
            :disabled="!!editingId"
            placeholder="e.g. fuad"
            required
          />
        </b-form-group>

        <b-form-group label="Full Name *">
          <b-form-input v-model.trim="modalForm.fullName" placeholder="e.g. Fuad Hossain" required />
        </b-form-group>

        <b-form-row>
          <b-col sm="6">
            <b-form-group label="Email">
              <b-form-input v-model.trim="modalForm.email" type="email" placeholder="user@inovacetech.com" />
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group label="Phone">
              <b-form-input v-model.trim="modalForm.phone" placeholder="01711-XXXXXX" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group label="Position">
          <b-form-input v-model.trim="modalForm.position" placeholder="e.g. Support Engineer" />
        </b-form-group>

        <b-alert v-if="modalError" show variant="danger" class="mt-2 mb-0">{{ modalError }}</b-alert>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
        <b-button :variant="editingId ? 'primary' : 'info'" :disabled="modalSaving" @click="ok()">
          <b-spinner v-if="modalSaving" small class="mr-1" />
          {{ modalSaving ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Member') }}
        </b-button>
      </template>
    </b-modal>

    <!-- Confirm Deactivate/Activate Modal -->
    <b-modal
      v-model="showConfirm"
      :title="confirmAction === 'deactivate' ? 'Deactivate Member' : 'Activate Member'"
      :ok-variant="confirmAction === 'deactivate' ? 'danger' : 'success'"
      :ok-title="confirmAction === 'deactivate' ? 'Deactivate' : 'Activate'"
      @ok="confirmToggle"
      cancel-variant="outline-secondary"
    >
      <p class="mb-0">
        Are you sure you want to
        <strong :class="confirmAction === 'deactivate' ? 'text-danger' : 'text-success'">
          {{ confirmAction }}
        </strong>
        <strong>{{ confirmTarget && confirmTarget.full_name }}</strong>?
        <span v-if="confirmAction === 'deactivate'">
          They will no longer appear in issue assignment dropdowns.
        </span>
      </p>
    </b-modal>

  </div>
</template>

<script>
import teamMemberService from '@/services/teamMemberService'

export default {
  name: 'TeamMembers',
  data: function() {
    return {
      loading: false,
      members: [],
      showModal: false,
      editingId: null,
      modalSaving: false,
      modalError: '',
      modalForm: { username: '', fullName: '', email: '', phone: '', position: '' },
      showConfirm: false,
      confirmAction: '',
      confirmTarget: null,
      fields: [
        { key: 'full_name',  label: 'Member',    sortable: true },
        { key: 'email',      label: 'Email' },
        { key: 'phone',      label: 'Phone' },
        { key: 'position',   label: 'Position',  sortable: true },
        { key: 'is_active',  label: 'Status',    sortable: true },
        { key: 'actions',    label: '',           thStyle: 'width:90px' },
      ]
    }
  },
  created: function() { this.loadMembers() },
  methods: {
    loadMembers: function() {
      var self = this
      self.loading = true
      teamMemberService.listAll(
        function(data) { self.members = data || []; self.loading = false },
        function(err)  {
          self.loading = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    openCreate: function() {
      this.editingId  = null
      this.modalForm  = { username: '', fullName: '', email: '', phone: '', position: '' }
      this.modalError = ''
      this.showModal  = true
    },
    openEdit: function(member) {
      this.editingId  = member.id
      this.modalForm  = {
        username: member.username,
        fullName: member.full_name,
        email:    member.email    || '',
        phone:    member.phone    || '',
        position: member.position || ''
      }
      this.modalError = ''
      this.showModal  = true
    },
    resetModal: function() {
      this.editingId  = null
      this.modalError = ''
      this.modalSaving = false
    },
    submitModal: function(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.modalForm.username || !this.modalForm.fullName) {
        this.modalError = 'Username and Full Name are required.'
        return
      }
      var self = this
      self.modalSaving = true
      self.modalError  = ''

      var onSuccess = function() {
        self.modalSaving = false
        self.showModal   = false
        self.loadMembers()
        self.$bvToast.toast(
          self.editingId ? 'Member updated.' : 'Member added.',
          { title: 'Saved', variant: 'success', solid: true }
        )
      }
      var onError = function(err) {
        self.modalSaving = false
        self.modalError  = err
      }

      if (self.editingId) {
        teamMemberService.update(self.editingId, self.modalForm, onSuccess, onError)
      } else {
        teamMemberService.create(self.modalForm, onSuccess, onError)
      }
    },
    toggleActive: function(member) {
      this.confirmTarget = member
      this.confirmAction = member.is_active ? 'deactivate' : 'activate'
      this.showConfirm   = true
    },
    confirmToggle: function() {
      var self    = this
      var member  = self.confirmTarget
      var handler = member.is_active
        ? function(s, e) { teamMemberService.deactivate(member.id, s, e) }
        : function(s, e) { teamMemberService.activate(member.id, s, e) }

      handler(
        function() {
          self.loadMembers()
          self.$bvToast.toast(
            member.full_name + ' ' + (member.is_active ? 'deactivated' : 'activated') + '.',
            { title: 'Updated', variant: 'success', solid: true }
          )
        },
        function(err) {
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    }
  }
}
</script>

<style scoped>
.avatar-sm { width: 36px; height: 36px; border-radius: 50%; background: #17a2b8; color: #fff;
             display: inline-flex; align-items: center; justify-content: center;
             font-size: .95rem; font-weight: 700; flex-shrink: 0; }
</style>
